import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { ZoomField } from './ZoomField';
import { BlobticZoomPopup } from './BlobticZoomPopup';
import { useBlobticZoom } from '../hooks/useBlobticZoom';
import { ZoomData } from '../services/blobticZoomService';

export const ZoomButtonTest: React.FC = () => {
  const [formData, setFormData] = useState({
    email: 'test@example.com',
    password: 'test123',
    agencyCode: ''
  });

  const [selectedAgency, setSelectedAgency] = useState<ZoomData | null>(null);
  const [testResults, setTestResults] = useState<string[]>([]);

  // Hook pour gérer le zoom
  const {
    openZoom,
    closeZoom,
    isOpen,
    currentZoomKey,
    currentDynamicParams
  } = useBlobticZoom();

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAgencyChange = (value: string, selectedData?: ZoomData) => {
    setFormData(prev => ({
      ...prev,
      agencyCode: value
    }));
    setSelectedAgency(selectedData || null);
    
    // Ajouter un résultat de test
    if (selectedData) {
      setTestResults(prev => [...prev, `✅ Agence sélectionnée: ${selectedData.DOS_LIB} (${selectedData.DOS_CODE})`]);
    }
  };

  const handleZoomOpen = () => {
    console.log('Ouverture du zoom avec:', {
      email: formData.email,
      password: formData.password
    });
    
    // Ajouter un résultat de test
    setTestResults(prev => [...prev, `🔍 Ouverture du zoom des agences...`]);
    
    // Ouvrir le zoom des agences avec les paramètres de connexion
    openZoom('dossier', {
      USER_MAIL: formData.email,
      USER_PASS: formData.password
    });
  };

  const handleZoomSelect = (selectedData: ZoomData) => {
    console.log('Agence sélectionnée:', selectedData);
    handleAgencyChange(selectedData.DOS_CODE || '', selectedData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.agencyCode) {
      setTestResults(prev => [...prev, `❌ Formulaire incomplet - tous les champs sont requis`]);
      return;
    }

    setTestResults(prev => [...prev, `✅ Formulaire soumis avec succès!`]);
    console.log('Formulaire soumis:', formData);
  };

  const clearTestResults = () => {
    setTestResults([]);
  };

  const isFormValid = formData.email && formData.password && formData.agencyCode;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Test du Bouton Zoom - Code d'Agence
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Testez le bouton zoom dans le champ "Code d'agence" en cliquant sur l'icône 🔍
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mot de passe"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Instructions :</strong> Cliquez sur le bouton 🔍 (icône zoom) pour ouvrir la liste des agences
              </Typography>
              <ZoomField
                zoomKey="dossier"
                label="Code d'agence"
                placeholder="Cliquez sur le bouton zoom pour sélectionner une agence"
                value={formData.agencyCode}
                onChange={handleAgencyChange}
                displayField="DOS_CODE"
                valueField="DOS_IDF"
                required
                fullWidth
                onZoomOpen={handleZoomOpen}
                showSelectedInfo={true}
                helperText="Utilisez le bouton zoom pour sélectionner votre agence"
              />
            </Grid>

            {selectedAgency && (
              <Grid item xs={12}>
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>Agence sélectionnée :</strong> {selectedAgency.DOS_LIB}
                    <br />
                    <strong>Code :</strong> {selectedAgency.DOS_CODE}
                    <br />
                    <strong>ID :</strong> {selectedAgency.DOS_IDF}
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!isFormValid}
                sx={{ mt: 2 }}
              >
                Tester la soumission
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Section des résultats de test */}
        {testResults.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Résultats des tests</Typography>
              <Button size="small" onClick={clearTestResults}>
                Effacer
              </Button>
            </Box>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
              {testResults.map((result, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  {result}
                </Typography>
              ))}
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Popup de zoom */}
      <BlobticZoomPopup
        open={isOpen}
        zoomKey={currentZoomKey || ''}
        dynamicParams={currentDynamicParams}
        onClose={closeZoom}
        onSelect={handleZoomSelect}
        title="Sélection d'agence"
      />
    </Box>
  );
};
